import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styles from "./StoreAnalysis.module.scss";
import {
  Card,
  Table,
  Select,
  Input,
  Typography,
  Skeleton,
  Empty,
  Button,
  Modal,
} from "antd";
import { ArrowUpOutlined, ArrowDownOutlined, SearchOutlined } from "@ant-design/icons";
import { getStoreAnalysisLanding } from "../../../api/Services";
import type { StoreAnalysisLandingItem } from "../../../types/storeAnalysis";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../../helpers/FilterSearchHelper";
import {
  isVariationAvailable,
  variationNotAvailableLabel,
} from "../../../helpers/VariationHelper";

const { Title, Text } = Typography;

const fmt = (v: number) =>
  `$ ${v.toLocaleString("es-AR", { maximumFractionDigits: 0 })}`;
const fmtN = (v: number) => v.toLocaleString("es-AR");

const Var: React.FC<{ value?: number | null; suffix?: string }> = ({
  value,
  suffix = "%",
}) => {
  if (!isVariationAvailable(value)) return <span>{variationNotAvailableLabel()}</span>;
  const pos = value >= 0;
  return (
    <span
      className={`${styles.variation} ${pos ? styles.positive : styles.negative}`}
    >
      {pos ? <ArrowUpOutlined /> : <ArrowDownOutlined />}{" "}
      {pos ? "+" : ""}
      {value.toFixed(1)}
      {suffix}
    </span>
  );
};

const LandingTableSkeleton: React.FC = () => (
  <div className={styles.tableSkeleton}>
    {Array.from({ length: 8 }).map((_, i) => (
      <Skeleton
        key={i}
        active
        title={false}
        paragraph={{ rows: 1, width: ["100%"] }}
      />
    ))}
  </div>
);

const StoreAnalysis: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [period, setPeriod] = useState(30);
  const [sortBy, setSortBy] = useState("ventaBruta");
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend">("descend");
  const [compareMode, setCompareMode] = useState(false);
  const [selectedStoreIds, setSelectedStoreIds] = useState<number[]>([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState<StoreAnalysisLandingItem[]>([]);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();

  const handleFetch = useCallback(async () => {
    const requestId = beginRequest();
    setLoading(true);
    const apiSortBy = sortBy === "performance" ? "ventaBruta" : sortBy;
    const data = await getStoreAnalysisLanding({
      search: debouncedSearch || undefined,
      period,
      sortBy: apiSortBy,
      sortOrder: sortOrder === "ascend" ? "asc" : "desc",
    });
    if (isStaleRequest(requestId)) return;
    setStores(data.stores || []);
    setLoading(false);
  }, [debouncedSearch, period, sortBy, sortOrder, beginRequest, isStaleRequest]);

  useEffect(() => {
    handleFetch();
  }, [handleFetch]);

  const hasData = stores.length > 0;

  const columns = useMemo(
    () => [
      {
        title: t("StoreAnalysis-Store", "TIENDA"),
        key: "nombre",
        render: (_: unknown, r: StoreAnalysisLandingItem) => (
          <Text className={styles.storeLink}>{r.nombre}</Text>
        ),
      },
      {
        title: t("StoreAnalysis-GrossSales", "VENTA BRUTA"),
        key: "ventaBruta",
        align: "right" as const,
        render: (_: unknown, r: StoreAnalysisLandingItem) => (
          <div className={styles.cellStack}>
            <Text strong>{fmt(r.ventaBruta)}</Text>
            <Var value={r.ventaVar} />
          </div>
        ),
      },
      {
        title: t("StoreAnalysis-Units", "UNIDADES"),
        key: "unidades",
        align: "right" as const,
        render: (_: unknown, r: StoreAnalysisLandingItem) => (
          <div className={styles.cellStack}>
            <Text strong>{fmtN(r.unidades)}</Text>
            <Var value={r.unidadesVar} />
          </div>
        ),
      },
      {
        title: t("StoreAnalysis-AvgTicket", "TICKET PROMEDIO"),
        key: "ticketPromedio",
        align: "right" as const,
        render: (_: unknown, r: StoreAnalysisLandingItem) => (
          <div className={styles.cellStack}>
            <Text strong>{fmt(r.ticketPromedio)}</Text>
            <Var value={r.ticketPromVar} />
          </div>
        ),
      },
      {
        title: t("StoreAnalysis-BasketSize", "TAMAÑO CESTA"),
        key: "tamanoCesta",
        align: "right" as const,
        render: (_: unknown, r: StoreAnalysisLandingItem) => (
          <div className={styles.cellStack}>
            <Text strong>{r.tamanoCesta.toFixed(2)}</Text>
            <Var value={r.tamanoCestaVar} />
          </div>
        ),
      },
      {
        title: t("StoreAnalysis-Margin", "MARGEN"),
        key: "margen",
        align: "right" as const,
        render: (_: unknown, r: StoreAnalysisLandingItem) => (
          <div className={styles.cellStack}>
            <Text strong>{r.margenPct.toFixed(1)}%</Text>
            <Var value={r.margenVar} suffix=" pp" />
          </div>
        ),
      },
      {
        title: t("StoreAnalysis-Supply", "ABASTECIMIENTO"),
        key: "abastecimiento",
        align: "center" as const,
        width: 130,
        render: (_: unknown, r: StoreAnalysisLandingItem) =>
          r.abastecimiento ? (
            <span>{r.abastecimiento}</span>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: t("StoreAnalysis-Alerts", "ALERTAS"),
        key: "alertas",
        align: "center" as const,
        width: 90,
        render: (_: unknown, r: StoreAnalysisLandingItem) =>
          r.alertas != null && r.alertas > 0 ? (
            <span className={styles.alertBadge}>{r.alertas}</span>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
    ],
    [t]
  );

  return (
    <div className={styles.wrapper}>
      <Title level={3} className={styles.pageTitle}>
        {t("StoreAnalysis-Stores", "Tiendas")}
      </Title>

      <Card className={styles.filtersCard} size="small">
        <div className={styles.filtersRow}>
          <div className={`${styles.filterItem} ${styles.filterItemSearch}`}>
            <Text className={styles.filterLabel}>
              {t("StoreAnalysis-Search", "Buscar")}
            </Text>
            <Input
              placeholder={t("StoreAnalysis-Store", "Tienda")}
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={styles.searchInput}
              allowClear
            />
          </div>
          <div className={styles.filtersTrailing}>
            <div className={`${styles.filterItem} ${styles.filterItemCompact}`}>
              <Text className={styles.filterLabel}>
                {t("StoreAnalysis-Period", "Período")}
              </Text>
              <Select
                value={period}
                onChange={setPeriod}
                className={styles.filterSelect}
                options={[
                  { value: 30, label: t("StoreAnalysis-Last30Days", "Últimos 30 días") },
                  { value: 60, label: t("StoreAnalysis-Last60Days", "Últimos 60 días") },
                  { value: 90, label: t("StoreAnalysis-Last90Days", "Últimos 90 días") },
                  { value: 180, label: t("StoreAnalysis-Last180Days", "Últimos 180 días") },
                ]}
              />
            </div>
            <div className={`${styles.filterItem} ${styles.filterItemCompact}`}>
              <Text className={styles.filterLabel}>
                {t("StoreAnalysis-Sort", "Orden")}
              </Text>
              <Select
                value={sortBy}
                onChange={setSortBy}
                className={styles.filterSelect}
                options={[
                  {
                    value: "ventaBruta",
                    label: t("StoreAnalysis-SortPerformance", "Performance"),
                  },
                  { value: "unidades", label: t("StoreAnalysis-Units", "Unidades") },
                  { value: "margen", label: t("StoreAnalysis-Margin", "Margen") },
                  { value: "nombre", label: t("StoreAnalysis-SortName", "Nombre") },
                ]}
              />
            </div>
            <div className={`${styles.filterItem} ${styles.viewFilter}`}>
              <Text className={styles.filterLabel}>
                {t("StoreAnalysis-View", "Vista")}
              </Text>
              <Button
                type="default"
                className={`${styles.compareBtnPill} ${
                  compareMode ? styles.compareBtnActive : ""
                }`}
                onClick={() => {
                  setCompareMode((active) => !active);
                  setSelectedStoreIds([]);
                }}
              >
                {t("StoreAnalysis-CompareStores", "Comparar tiendas")}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card className={styles.tableCard}>
        {loading && !hasData ? (
          <LandingTableSkeleton />
        ) : (
          <Table
            columns={columns}
            dataSource={stores.map((s) => ({ ...s, key: s.id }))}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            size="middle"
            className={styles.dataTable}
            loading={loading && hasData}
            locale={{
              emptyText: (
                <Empty description={t("StoreAnalysis-NoStores", "Sin tiendas")} />
              ),
            }}
            onChange={(_p, _f, sorter: any) => {
              if (sorter?.order) {
                setSortOrder(sorter.order);
              }
            }}
            rowSelection={
              compareMode
                ? {
                    selectedRowKeys: selectedStoreIds,
                    onChange: (keys) => setSelectedStoreIds(keys as number[]),
                  }
                : undefined
            }
            onRow={(record) => ({
              onClick: () => {
                if (compareMode) return;
                navigate(`/commercial-analysis/stores/${record.id}`);
              },
              className: styles.tableRow,
            })}
            footer={
              compareMode && selectedStoreIds.length >= 2
                ? () => (
                    <div className={styles.compareFooter}>
                      <Text>
                        {t(
                          "StoreAnalysis-SelectedStores",
                          "{{count}} tiendas seleccionadas",
                          {
                            count: selectedStoreIds.length,
                          }
                        )}
                      </Text>
                      <Button
                        type="primary"
                        className={styles.compareBtn}
                        onClick={() => setCompareModalOpen(true)}
                      >
                        {t("StoreAnalysis-ViewComparison", "Ver comparación")}
                      </Button>
                    </div>
                  )
                : undefined
            }
          />
        )}
      </Card>

      <Modal
        visible={compareModalOpen}
        title={t("StoreAnalysis-CompareStores", "Comparar tiendas")}
        onCancel={() => setCompareModalOpen(false)}
        footer={null}
        width={960}
        destroyOnClose
      >
        <Table
          size="small"
          pagination={false}
          className={styles.dataTable}
          dataSource={stores
            .filter((s) => selectedStoreIds.includes(s.id))
            .map((s) => ({ ...s, key: s.id }))}
          columns={[
            {
              title: t("StoreAnalysis-Store", "TIENDA"),
              dataIndex: "nombre",
              key: "nombre",
            },
            {
              title: t("StoreAnalysis-GrossSales", "VENTA BRUTA"),
              key: "ventaBruta",
              align: "right" as const,
              render: (_: unknown, r: StoreAnalysisLandingItem) => (
                <div className={styles.cellStack}>
                  <Text strong>{fmt(r.ventaBruta)}</Text>
                  <Var value={r.ventaVar} />
                </div>
              ),
            },
            {
              title: t("StoreAnalysis-Units", "UNIDADES"),
              key: "unidades",
              align: "right" as const,
              render: (_: unknown, r: StoreAnalysisLandingItem) => (
                <div className={styles.cellStack}>
                  <Text strong>{fmtN(r.unidades)}</Text>
                  <Var value={r.unidadesVar} />
                </div>
              ),
            },
            {
              title: t("StoreAnalysis-Margin", "MARGEN"),
              key: "margen",
              align: "right" as const,
              render: (_: unknown, r: StoreAnalysisLandingItem) => (
                <div className={styles.cellStack}>
                  <Text strong>{r.margenPct.toFixed(1)}%</Text>
                  <Var value={r.margenVar} suffix=" pp" />
                </div>
              ),
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default StoreAnalysis;
